import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { sessionSignal, onSessionEnd, accountEpoch } from './sessionEnded'
import { captureRequestIdentity, actingEchoVersion, lastActingOrgEmail, type RequestIdentity } from '@papan/api-client'
import { isTypingTarget } from './keyboard'
import { useToast } from './Toast'
import { useTranslation } from '@papan/i18n'
import { TENANT_WRITE_FROZEN_CODE, damagedMessageFrom } from '@papan/types'

export type UndoOperationType = 'delete' | 'deleteSplit' | 'move' | 'moveSplit' | 'moveAcrossOrgs' | 'rename' | 'copy' | 'createFolder' | 'addVersion' | 'extract'

interface BaseUndoOperation {
  id: string
  timestamp: number
  /** D134 — the identity the operation was DONE as (captured when it was recorded): its undo acts as exactly this,
   *  whatever the tab shows when Ctrl+Z is pressed. */
  identity?: RequestIdentity
  /** …and the signed-in account then (`accountEpoch`): an identity with no org email (the server picks it) is never
   *  sent once the account changed — it would act as the new account. */
  account?: number
}

interface DeleteOperation extends BaseUndoOperation {
  type: 'delete'
  binId: string
  /**
   * Original file id. Lets the delete-undo handler tell apart the two
   * 404 shapes from restoreFromBin: bin entry gone because the user
   * already manually restored (file id still present in drive → undo
   * is a silent no-op) vs. bin entry gone because the file was purged
   * (file id absent → undo surfaces the error). Added 2026-05-18.
   */
  fileId: string
  name: string
}

/**
 * #801 — a SPLIT delete: the folder held branches the caller can't change, so
 * the server binned only the removable branches (one bin item each) and left
 * the root. Undo restores every one of them. `binIds[i]` pairs with
 * `fileIds[i]` for the same per-item purge detection a whole delete has.
 */
interface DeleteSplitOperation extends BaseUndoOperation {
  type: 'deleteSplit'
  binIds: string[]
  fileIds: string[]
  rootId: string
  name: string
}

interface MoveOperation extends BaseUndoOperation {
  type: 'move'
  fileId: string
  originalParentId: string | null
  name: string
  /** Set when the move-back's job was lost (#1167 S4): it may have run, so a retry finds the item already back = done. */
  moveUnconfirmedIds?: string[]
}

/** A move ACROSS organisations (owner, 2026-10-06): the item landed in the other drive (`newFileRef`,
 *  the id it is deleted by there) and its source went to its owning tenant's Bin (`binId`). Undo
 *  brings the source back from that Bin, then takes the landed item out. */
interface MoveAcrossOrgsOperation extends BaseUndoOperation {
  type: 'moveAcrossOrgs'
  newFileRef: string
  binId: string
  name: string
  /** The moved item's own id (a retry checks whether it is back). */
  sourceFileId?: string
  /** D134 — the undo's progress, so a retry after a partial run finishes it: the source is back. */
  sourceRestored?: boolean
  /** The landed item was taken out (a retry after it does nothing more). */
  deletedIds?: string[]
  /** Ids whose delete job was lost (it may have finished): a retry's 404 through a mount then stays "couldn't confirm". */
  deleteUnconfirmedIds?: string[]
}

/**
 * #801 — a SPLIT move: the root stayed where it was; only `moved` went under
 * a new shell (`destFolderId`) at the destination. Undo moves them back under
 * the root and removes the shell when it's provably empty.
 */
interface MoveSplitOperation extends BaseUndoOperation {
  type: 'moveSplit'
  rootId: string
  destFolderId: string
  moved: string[]
  name: string
  /** Moved ids whose move-back job was lost (#1167 S4): a retry finds one already under the root = done. */
  moveUnconfirmedIds?: string[]
}

interface RenameOperation extends BaseUndoOperation {
  type: 'rename'
  fileId: string
  originalName: string
  newName: string
}

/** Files a Replace wrote a new version into (owner, 2026-10-06): undo restores the version each one
 *  replaced — never deletes the file. */
/** The code an undo handler throws when it can't confirm what its background job did (#921). */
export const UNDO_UNCONFIRMED_CODE = 'UNDO_UNCONFIRMED'

/** The error for {@link UNDO_UNCONFIRMED_CODE}; its message is for logs, the toast is localized from the code. */
export function undoUnconfirmedError(name: string, where: 'bin' | 'drive' = 'bin'): Error & { code: typeof UNDO_UNCONFIRMED_CODE; where: 'bin' | 'drive' } {
  // `where` says where the person should look: the Bin for a restore/delete, the drive (refresh) for a move back.
  return Object.assign(new Error(`Could not confirm the undo of "${name}" — check the ${where === 'drive' ? 'drive' : 'Bin'}`), { code: UNDO_UNCONFIRMED_CODE, where } as const)
}

/**
 * The undo-failure toast. A refusal the SERVER explained (a 4xx carrying a message — e.g. "This file
 * has changed since — open Version History…") is shown with its reason; anything else (a network
 * failure, a crash) stays the generic line.
 */
export function undoFailureText(t: (key: string, opts?: Record<string, unknown>) => string, action: string, err: unknown): string {
  // #1397: a damaged stored copy (undo-replace's previous version) — the server's whole sentence, never inside ours
  const damaged = damagedMessageFrom(err)
  if (damaged) return damaged
  const e = err as { status?: unknown; message?: unknown; code?: unknown } | null
  // E7 PR 0b: a restore that met an app database still being checked — nothing was done; no automatic retry (owner,
  // option B): the person tries again in a moment (the undo stays on the stack)
  if (e?.code === 'ENGINE_DB_VERIFYING') return t('undo.failedDbVerifying', { action })
  // D134: the identity it was done as can't be named (or the account changed) — it never acts as another one
  if (e?.code === 'UNDO_IDENTITY_UNKNOWN' || e?.code === 'UNDO_ACCOUNT_CHANGED') return t('undo.failedIdentity', { action })
  // An undo that ran a background job whose outcome nobody can confirm (the job's result is gone):
  // say so, and where to look — never the generic "failed", which reads as "nothing happened" (#921).
  if (e?.code === UNDO_UNCONFIRMED_CODE) return t((e as { where?: unknown }).where === 'drive' ? 'undo.unconfirmedMove' : 'undo.unconfirmed', { action })
  const reason = typeof e?.status === 'number' && e.status >= 400 && e.status < 500 && typeof e.message === 'string' ? e.message.trim() : ''
  return reason ? t('undo.failedReason', { action, reason }) : t('undo.failed', { action })
}

export interface VersionedUndoItem {
  fileId: string
  previousVersionId: string
  /** The version the Replace wrote: undo restores only while it is still the file's current version
   *  (else "changed since — open Version History"). */
  versionId?: string
}

interface CopyOperation extends BaseUndoOperation {
  type: 'copy'
  copiedFileIds: string[]
  /** One paste is ONE undo step: the new items are deleted AND the replaced files restored. */
  versioned?: VersionedUndoItem[]
  /** The undo already restored `versioned` (a retry only finishes the rest). */
  versionsRestored?: boolean
  /** D134 — progress of the version restore (a retry skips what is done): the batched restore landed; legacy ones. */
  checkedRestored?: boolean
  legacyRestored?: string[]
  /** Ids the undo already deleted or found gone (a retry skips them). Never removed from `copiedFileIds`: the label
   *  counts it. */
  deletedIds?: string[]
  /** Ids whose delete job was lost (it may have finished): a retry's 404 through a mount then stays "couldn't confirm". */
  deleteUnconfirmedIds?: string[]
}

interface CreateFolderOperation extends BaseUndoOperation {
  type: 'createFolder'
  folderId: string
  name: string
  /** The folder was deleted or found gone (a retry does nothing more). */
  deletedIds?: string[]
  /** Ids whose delete job was lost (it may have finished): a retry's 404 through a mount then stays "couldn't confirm". */
  deleteUnconfirmedIds?: string[]
}

/** A Replace wrote a new version into an existing file (the user-facing "Replace"; the op keeps its
 *  internal `addVersion` name) — undo restores the version it replaced. */
interface AddVersionOperation extends BaseUndoOperation {
  type: 'addVersion'
  fileId: string
  /** Previous version ID to restore to */
  previousVersionId: string
  /** The version this Replace wrote — undo restores only while it is still current. */
  versionId?: string
  name: string
  /** A MOVE with Replace (the moved file's bytes became this version, the moved file went to the
   *  Bin): the same undo step also brings the moved file back from the Bin. */
  restoreSource?: { binId: string; fileId: string }
  /** D134 — progress of the legacy two-step undo: the version is restored. */
  versionRestored?: boolean
}

interface ExtractOperation extends BaseUndoOperation {
  type: 'extract'
  extractedFileIds: string[]
  createdFolderId: string | null
  name: string
  /** Existing files the extract replaced with a new version: undo restores each replaced version. */
  versioned?: VersionedUndoItem[]
  /** The undo already restored `versioned` (a retry only finishes the rest). */
  versionsRestored?: boolean
  checkedRestored?: boolean
  legacyRestored?: string[]
  /** Ids (files, then the created folder) the undo already deleted or found gone (a retry skips them). */
  deletedIds?: string[]
  /** Ids whose delete job was lost (it may have finished): a retry's 404 through a mount then stays "couldn't confirm". */
  deleteUnconfirmedIds?: string[]
}

export type UndoOperation =
  | DeleteOperation
  | DeleteSplitOperation
  | MoveOperation
  | MoveAcrossOrgsOperation
  | MoveSplitOperation
  | RenameOperation
  | CopyOperation
  | CreateFolderOperation
  | AddVersionOperation
  | ExtractOperation

type NewDeleteOperation = Omit<DeleteOperation, 'id' | 'timestamp'>
type NewDeleteSplitOperation = Omit<DeleteSplitOperation, 'id' | 'timestamp'>
type NewMoveOperation = Omit<MoveOperation, 'id' | 'timestamp'>
type NewMoveSplitOperation = Omit<MoveSplitOperation, 'id' | 'timestamp'>
type NewMoveAcrossOrgsOperation = Omit<MoveAcrossOrgsOperation, 'id' | 'timestamp'>
type NewRenameOperation = Omit<RenameOperation, 'id' | 'timestamp'>
type NewCopyOperation = Omit<CopyOperation, 'id' | 'timestamp'>
type NewCreateFolderOperation = Omit<CreateFolderOperation, 'id' | 'timestamp'>
type NewAddVersionOperation = Omit<AddVersionOperation, 'id' | 'timestamp'>
type NewExtractOperation = Omit<ExtractOperation, 'id' | 'timestamp'>

type NewUndoOperation =
  | NewDeleteOperation
  | NewDeleteSplitOperation
  | NewMoveOperation
  | NewMoveSplitOperation
  | NewMoveAcrossOrgsOperation
  | NewRenameOperation
  | NewCopyOperation
  | NewCreateFolderOperation
  | NewAddVersionOperation
  | NewExtractOperation

export { captureUndoContext, type UndoCapture } from './undoCapture'
import type { UndoCapture } from './undoCapture'

// Handler type for undo operations
/** What the provider hands a handler (D134): `identity` — the identity the operation was DONE as (pinned when it was
 *  recorded). Every request of the undo acts as it, so the undo runs to completion in its own session even if the
 *  tenant / identity switches meanwhile (after a sign-out its requests simply fail). The handler never stops between
 *  its steps, and each step is idempotent so a retry after a partial run finishes it. `sameAccount()`: still the
 *  account it was recorded under — an identity with no org email is never sent once it isn't. `outcome`: the handler
 *  counts what it could not confirm (an item no longer reachable), so the toast says so instead of a plain "Undone". */
export interface UndoHandlerContext {
  identity?: RequestIdentity
  sameAccount?: () => boolean
  outcome?: { unreachable: number }
}
export type UndoHandler<T extends UndoOperation = UndoOperation> = (operation: T, ctx?: UndoHandlerContext) => Promise<void>

// Registry of undo handlers - features register their handlers here
const undoHandlers: Map<UndoOperationType, UndoHandler> = new Map()

/**
 * Register an undo handler for a specific operation type.
 * Features should call this on module load to register their handlers.
 */
export function registerUndoHandler<T extends UndoOperationType>(
  type: T,
  handler: UndoHandler<Extract<UndoOperation, { type: T }>>
): void {
  undoHandlers.set(type, handler as UndoHandler)
}

/**
 * Check if a handler is registered for a specific operation type.
 */
export function hasUndoHandler(type: UndoOperationType): boolean {
  return undoHandlers.has(type)
}

// ─── Active Editor Undo/Redo ──────────────────────────────────────
// Editors register their undo/redo while they are the active tab. Ctrl+Z is
// answered by the pane the user is IN (see `UndoRegion` and
// resolveUndoTarget), which is the same way the workspace already routes
// every other shortcut through its command scopes.
//
// Inside an editor, `editable` decides who owns the keystroke:
//
//   editable, has history  → it answers
//   editable, no history   → Ctrl+Z does nothing. The user is in a document
//                            they can change; reaching past it to reverse a
//                            file operation somewhere else is the failure this
//                            whole mechanism exists to remove — and "nothing
//                            to undo right now" is not "not my keystroke".
//   not editable           → a viewer (PDF, image, read-only tab). Nothing
//                            here to confuse a file operation with, so the
//                            drive keeps its undo.

/** Which pane owns Ctrl+Z right now. */
export type UndoRegion = 'editor' | 'chrome'

export interface EditorUndoRedoState {
  canUndo: boolean
  canRedo: boolean
}

export interface EditorUndoRedo {
  undo: () => Promise<boolean>
  redo: () => Promise<boolean>
  /** Optional label for the Edit menu, e.g. "Spreadsheet" */
  label?: string
  /**
   * This surface can be CHANGED by the user, so Ctrl+Z belongs to it while
   * they are in it — even at the moments its own history happens to be empty.
   * Only a viewer that cannot be edited at all leaves it unset, and the
   * drive's undo answers instead.
   */
  editable?: boolean
  /** Subscribe to undo/redo availability changes. Returns unsubscribe fn. */
  onStateChange?: (cb: (state: EditorUndoRedoState) => void) => () => void
}

interface UndoContextValue {
  canUndo: boolean
  canRedo: boolean
  /** Short label for the next undo action, e.g. 'Delete "file.txt"' */
  undoLabel: string | null
  redoLabel: string | null
  recordDelete: (at: UndoCapture, binId: string, fileId: string, name: string) => void
  recordMove: (at: UndoCapture, fileId: string, originalParentId: string | null, name: string) => void
  /**
   * #801 — record a folder/file delete from the SERVER'S ANSWER: a whole delete
   * (`binId`) pushes `delete`; a split one (no `binId`) pushes `deleteSplit`
   * over every bin item. Nothing binned ⇒ nothing recorded (the caller toasts
   * "nothing left"). Typed structurally — core-ui can't import the files types.
   */
  recordDeleteResult: (at: UndoCapture, fileId: string, name: string, result: DeleteResultLike) => void
  /**
   * #801 — record a move from the SERVER'S ANSWER: no `split` pushes `move`; a
   * split pushes `moveSplit`; a replayed split (`split.replay`) records NOTHING
   * (the first request already did it — the caller toasts "nothing left to move").
   */
  recordMoveResult: (at: UndoCapture, fileId: string, originalParentId: string | null, name: string, result: MoveResultLike) => void
  recordRename: (at: UndoCapture, fileId: string, originalName: string, newName: string) => void
  recordCopy: (at: UndoCapture, copiedFileIds: string[], versioned?: VersionedUndoItem[]) => void
  recordCreateFolder: (at: UndoCapture, folderId: string, name: string) => void
  recordAddVersion: (at: UndoCapture, fileId: string, previousVersionId: string, name: string, restoreSource?: { binId: string; fileId: string }, versionId?: string) => void
  recordExtract: (at: UndoCapture, extractedFileIds: string[], createdFolderId: string | null, name: string, versioned?: VersionedUndoItem[]) => void
  undo: () => Promise<void>
  redo: () => Promise<void>
  /** Register an active editor's undo/redo. Returns unregister function. */
  registerEditor: (editor: EditorUndoRedo) => () => void
  /** Tell the undo system which pane the user is working in. Call it from the
   *  same place that sets a command scope — see useUndoRegion. */
  setUndoRegion: (region: UndoRegion) => void
  /** Whether an editor is currently registered (for menu label logic) */
  hasActiveEditor: boolean
}

/** The part of a delete response `recordDeleteResult` reads (#801). */
export interface DeleteResultLike {
  binId?: string
  binIds: string[]
  binnedFileIds: string[]
}

/** The part of a move response `recordMoveResult` reads (#801). */
export interface MoveResultLike {
  split?: { replay?: boolean; destFolderId?: string | null; moved: string[] }
  /** A move across organisations (the source binned in its own tenant, the item landed there). */
  movedAcrossOrgs?: { newFileRef: string; binId?: string; sourceTenantId: string }
}

function getUndoMenuLabel(op: UndoOperation): string {
  switch (op.type) {
    case 'delete': return `Delete "${op.name}"`
    case 'deleteSplit': return `Delete part of "${op.name}"`
    case 'move': return `Move "${op.name}"`
    case 'moveAcrossOrgs': return `Move "${op.name}"`
    case 'moveSplit': return `Move part of "${op.name}"`
    case 'rename': return `Rename "${op.originalName}"`
    case 'copy': {
      // A paste whose Replace wrote versions replaced files: name it so.
      const c = op.copiedFileIds.length, r = op.versioned?.length ?? 0, n = c + r
      if (r === 0) return n > 1 ? `Copy (${n} items)` : 'Copy'
      if (c === 0) return r > 1 ? `Replace (${r} files)` : 'Replace'
      return `Copy and Replace (${n} items)`
    }
    case 'createFolder': return `Create Folder "${op.name}"`
    case 'addVersion': return op.restoreSource ? `Move "${op.name}"` : `Replace "${op.name}"`
    case 'extract': return `Extract "${op.name}"`
  }
}

const UndoContext = createContext<UndoContextValue | null>(null)

export function useUndo() {
  const context = useContext(UndoContext)
  if (!context) {
    throw new Error('useUndo must be used within an UndoProvider')
  }
  return context
}

const MAX_UNDO_STACK_SIZE = 50
/** How long an undo runs before its progress toast opens (a quick undo shows only its result). */
export const UNDO_PROGRESS_DELAY_MS = 1500
/** The one id the "an undo is still running" notice shows under — a repeated press replaces it, never stacks. */
const UNDO_STILL_RUNNING_TOAST_ID = 'undo-still-running'

function generateId(): string {
  return `undo-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/**
 * Mark a container as one undo region, the way useFocusScope marks one command
 * scope: a mousedown anywhere inside it tells the undo system the user is
 * working there.
 *
 * Mousedown, not focus: most of an editor's chrome — a toolbar button, a
 * colour swatch, a grid cell — never takes focus, and the pane the user just
 * clicked is what they mean by "undo".
 */
export function useUndoRegion(
  region: UndoRegion,
  containerRef: React.RefObject<HTMLElement | null>,
): void {
  const { setUndoRegion } = useUndo()
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onMouseDown = () => setUndoRegion(region)
    el.addEventListener('mousedown', onMouseDown, true)
    return () => el.removeEventListener('mousedown', onMouseDown, true)
  }, [region, containerRef, setUndoRegion])
}

export function UndoProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<UndoOperation[]>([])
  const toast = useToast()
  const { t } = useTranslation('core-ui')
  // The running undo, if any — one at a time, PER SESSION (#1316 review round 2, r2 A-M1): it holds the lock only
  // while the session it ran in lasts. An undo of the previous session still finishing pinned to its identity (D134)
  // can't be seen in the next one, so it never answers that session's Ctrl+Z with "still running"; when it ends it
  // releases only its own lock.
  const runningUndoRef = useRef<{ signal: AbortSignal } | null>(null)
  // D134: the stack belongs to the session — when it ends (sign-out, another tenant / identity) it is cleared, so
  // nothing recorded under one account is ever undone (or retried) under another
  useEffect(() => onSessionEnd(() => setStack([])), [])
  const activeEditorRef = useRef<EditorUndoRedo | null>(null)
  const editorUnsubRef = useRef<(() => void) | null>(null)
  const [hasActiveEditor, setHasActiveEditor] = useState(false)
  // Which pane the user is working in. Set by the same clicks that set a
  // command scope; 'chrome' is the default so the drive's own pages, which
  // mount no editor, behave exactly as they did before.
  const [region, setRegion] = useState<UndoRegion>('chrome')
  const [editorState, setEditorState] = useState<EditorUndoRedoState>({ canUndo: false, canRedo: false })

  const registerEditor = useCallback((editor: EditorUndoRedo) => {
    // Clean up previous subscription
    editorUnsubRef.current?.()
    editorUnsubRef.current = null

    activeEditorRef.current = editor
    setHasActiveEditor(true)
    setEditorState({ canUndo: false, canRedo: false })

    // Subscribe to state changes if supported
    if (editor.onStateChange) {
      editorUnsubRef.current = editor.onStateChange(setEditorState)
    }

    return () => {
      if (activeEditorRef.current === editor) {
        editorUnsubRef.current?.()
        editorUnsubRef.current = null
        activeEditorRef.current = null
        setHasActiveEditor(false)
        setEditorState({ canUndo: false, canRedo: false })
      }
    }
  }, [])

  const pushOperation = useCallback((at: UndoCapture, op: NewUndoOperation) => {
    // D134: the operation's session ended while its request was out (a switch, a sign-out) — its answer belongs to the
    // session that is gone: nothing is recorded (an undo now would act as the wrong session)
    if (at.signal.aborted) return
    // D134: the tab had no org email of its own (the server used its stored choice, which another tab may change):
    // pin the email the server said it acted as — only if what a no-email request gets has NOT CHANGED since the
    // capture (every answer in between named the same email); any change ⇒ the operation is UNPINNED (no email at all)
    // and its undo refuses ("switch back") rather than act as whatever is stored by then
    let identity = at.identity
    if (!identity.orgEmail && identity.actingOrgEmail && actingEchoVersion(identity.tenantId) !== identity.actingVersion) {
      const { actingOrgEmail: _dropped, actingVersion: _v, ...unpinned } = identity
      identity = unpinned
    } else if (!identity.orgEmail && !identity.shareSession && !identity.actingOrgEmail && actingEchoVersion(identity.tenantId) === 1) {
      // captured before ANY echo for the tenant (the tab's first operation there): exactly one consistent no-email
      // answer since — which the operation's own request could have produced — names it; any more is a change: unpinned
      const echoed = lastActingOrgEmail(identity.tenantId)
      if (echoed) identity = { ...identity, actingOrgEmail: echoed, actingVersion: 1 }
    }
    const operation = {
      ...op,
      id: generateId(),
      timestamp: Date.now(),
      // D134: the identity the operation's REQUEST was sent as (captured by the caller before it) — its undo acts as it
      identity,
      account: at.account,
    } as UndoOperation

    setStack((prev) => {
      const newStack = [operation, ...prev]
      // Keep only the most recent operations
      if (newStack.length > MAX_UNDO_STACK_SIZE) {
        return newStack.slice(0, MAX_UNDO_STACK_SIZE)
      }
      return newStack
    })
  }, [])

  const recordDelete = useCallback((at: UndoCapture, binId: string, fileId: string, name: string) => {
    pushOperation(at, { type: 'delete', binId, fileId, name })
  }, [pushOperation])

  const recordMove = useCallback((at: UndoCapture, fileId: string, originalParentId: string | null, name: string) => {
    pushOperation(at, { type: 'move', fileId, originalParentId, name })
  }, [pushOperation])

  const recordDeleteResult = useCallback((at: UndoCapture, fileId: string, name: string, result: DeleteResultLike) => {
    if (result.binId) {
      pushOperation(at, { type: 'delete', binId: result.binId, fileId, name })
      return
    }
    if (result.binIds.length === 0) return
    pushOperation(at, { type: 'deleteSplit', binIds: result.binIds, fileIds: result.binnedFileIds, rootId: fileId, name })
  }, [pushOperation])

  const recordMoveResult = useCallback((at: UndoCapture, fileId: string, originalParentId: string | null, name: string, result: MoveResultLike) => {
    if (result.movedAcrossOrgs) {
      if (result.movedAcrossOrgs.binId) pushOperation(at, { type: 'moveAcrossOrgs', newFileRef: result.movedAcrossOrgs.newFileRef, binId: result.movedAcrossOrgs.binId, name, sourceFileId: fileId })
      return
    }
    const split = result.split
    if (!split) {
      pushOperation(at, { type: 'move', fileId, originalParentId, name })
      return
    }
    if (split.replay || !split.destFolderId || split.moved.length === 0) return
    pushOperation(at, { type: 'moveSplit', rootId: fileId, destFolderId: split.destFolderId, moved: split.moved, name })
  }, [pushOperation])

  const recordRename = useCallback((at: UndoCapture, fileId: string, originalName: string, newName: string) => {
    pushOperation(at, { type: 'rename', fileId, originalName, newName })
  }, [pushOperation])

  const recordCopy = useCallback((at: UndoCapture, copiedFileIds: string[], versioned?: VersionedUndoItem[]) => {
    pushOperation(at, { type: 'copy', copiedFileIds, ...(versioned?.length ? { versioned } : {}) })
  }, [pushOperation])

  const recordCreateFolder = useCallback((at: UndoCapture, folderId: string, name: string) => {
    pushOperation(at, { type: 'createFolder', folderId, name })
  }, [pushOperation])

  const recordAddVersion = useCallback((at: UndoCapture, fileId: string, previousVersionId: string, name: string, restoreSource?: { binId: string; fileId: string }, versionId?: string) => {
    pushOperation(at, { type: 'addVersion', fileId, previousVersionId, name, ...(restoreSource ? { restoreSource } : {}), ...(versionId ? { versionId } : {}) })
  }, [pushOperation])

  const recordExtract = useCallback((at: UndoCapture, extractedFileIds: string[], createdFolderId: string | null, name: string, versioned?: VersionedUndoItem[]) => {
    pushOperation(at, { type: 'extract', extractedFileIds, createdFolderId, name, ...(versioned?.length ? { versioned } : {}) })
  }, [pushOperation])

  const getActionDescription = useCallback((op: UndoOperation): string => {
    switch (op.type) {
      case 'delete':
        return t('undo.actions.delete', { name: op.name })
      case 'deleteSplit':
        return t('undo.actions.deleteSplit', { name: op.name })
      case 'move':
      case 'moveAcrossOrgs':
        return t('undo.actions.move', { name: op.name })
      case 'moveSplit':
        return t('undo.actions.moveSplit', { name: op.name })
      case 'rename':
        return t('undo.actions.rename', { oldName: op.originalName, newName: op.newName })
      case 'copy': {
        // A paste whose Replace wrote versions REPLACED files — say so, never "copied" alone.
        const copied = op.copiedFileIds.length
        const replaced = op.versioned?.length ?? 0
        if (replaced === 0) return t('undo.actions.copy', { count: copied })
        if (copied === 0) return t('undo.actions.replacedFiles', { count: replaced })
        return t('undo.actions.copyAndReplace', { copied, replaced })
      }
      case 'createFolder':
        return t('undo.actions.createFolder', { name: op.name })
      case 'addVersion':
        return op.restoreSource ? t('undo.actions.move', { name: op.name }) : t('undo.actions.replace', { name: op.name })
      case 'extract':
        return t('undo.actions.extract', { name: op.name })
    }
  }, [t])

  /**
   * Who answers the next Ctrl+Z — the ONE place that decides, so the Edit
   * menu can never offer something different from what the keystroke does.
   *
   *   'editor'  the editor's own history
   *   'drive'   a file operation
   *   'nobody'  neither can answer; the keystroke stops here
   *
   * WHERE THE USER IS decides, not which action was more recent. The
   * workspace already works this way for everything else: clicking the
   * explorer or an editor sets a command scope, and shortcuts route by it
   * (CommandRegistry). Undo was the one thing wired outside that, and the
   * result was a keystroke pressed in one pane acting on another. Region
   * mirrors the same idea for undo, and it answers "which of these did the
   * user mean" with something they can see — the pane they just clicked —
   * instead of a timestamp comparison they cannot.
   */
  const resolveUndoTarget = useCallback((): 'editor' | 'drive' | 'nobody' => {
    const editor = activeEditorRef.current

    // Outside the editor — the drive's own chrome. Its operations are the
    // only history here.
    if (region !== 'editor') return stack.length > 0 ? 'drive' : 'nobody'

    // Inside an editor. Three positions, and only the middle one needs the
    // keystroke stopped:
    //
    //   registers a history  → it answers
    //   editable, no history → stop here; reaching past it would reverse a
    //                          file operation the user is not looking at
    //   registers nothing    → an uneditable viewer (PDF, image, read-only).
    //                          Nothing here to confuse a file operation with,
    //                          so the drive keeps its undo.
    if (!editor) return stack.length > 0 ? 'drive' : 'nobody'
    if (editorState.canUndo) return 'editor'
    if (editor.editable) return 'nobody'
    return stack.length > 0 ? 'drive' : 'nobody'
  }, [stack, editorState, region])

  const undo = useCallback(async () => {
    const target = resolveUndoTarget()
    if (target === 'nobody') return
    if (target === 'editor') {
      await activeEditorRef.current!.undo()
      return
    }

    if (stack.length === 0) return
    // A Ctrl+Z while the last undo still runs (a big folder's move-back takes minutes, #1167 S4) is not
    // queued — say so rather than swallow it.
    if (runningUndoRef.current && !runningUndoRef.current.signal.aborted) {
      // Under ONE id: pressing again replaces the notice instead of stacking another.
      toast.info(t('undo.stillRunning'), undefined, undefined, { id: UNDO_STILL_RUNNING_TOAST_ID })
      return
    }

    const operation = stack[0]
    const handler = undoHandlers.get(operation.type)

    if (!handler) {
      console.error(`[Undo] No handler registered for operation type: ${operation.type}`)
      toast.error(t('undo.noHandler', { type: operation.type }))
      return
    }

    // D134: the undo runs to completion AS the session that started it (its identity pinned on every request) — never
    // stopped between its steps; only what it SHOWS is dropped once that session ended (it names the old account's item)
    const signal = sessionSignal()
    const run = { signal }
    runningUndoRef.current = run
    // A slow undo (a big folder's move-back runs as a job, minutes — #1167 S4) shows a progress toast while it
    // runs; opened only once it has run UNDO_PROGRESS_DELAY_MS, so a quick undo shows nothing but its result.
    const identity = operation.identity ?? captureRequestIdentity()
    const account = operation.account ?? accountEpoch()
    const outcome = { unreachable: 0 }
    let progressId: string | null = null
    // #1316 review round 1: the delayed toast is part of what it SHOWS — never opened once the session ended (on the
    // sign-in page it would name the previous account's item), and its timer goes with the session
    const progressTimer = setTimeout(() => {
      if (signal.aborted) return
      progressId = toast.progress(t('undo.running', { action: getActionDescription(operation) }), null).id
    }, UNDO_PROGRESS_DELAY_MS)
    const stopProgressTimer = () => clearTimeout(progressTimer)
    signal.addEventListener('abort', stopProgressTimer, { once: true })
    try {
      await handler(operation, { identity, sameAccount: () => accountEpoch() === account, outcome })
      if (signal.aborted) return

      // Remove THIS operation on success — by id, not by position. The handler
      // can be a server round-trip, and anything recorded meanwhile lands at the
      // head: dropping the head then discarded the new operation and left the
      // undone one on top, where every later Ctrl+Z failed on it for good.
      setStack((prev) => prev.filter((o) => o.id !== operation.id))
      // D134: honest about what it could not confirm (an item gone already, or one this identity can no longer reach)
      toast.success(outcome.unreachable > 0
        ? t('undo.successUnreachable', { action: getActionDescription(operation) })
        : t('undo.success', { action: getActionDescription(operation) }))
    } catch (err) {
      // The session it ran for ended (sign-out, another tenant / identity): nothing to say to anyone.
      if (signal.aborted) return
      // Keep the operation in stack on failure so user can retry.
      // Uploads paused (402 TENANT_WRITE_FROZEN — undoing a delete re-creates the item)
      // is said as such, localized from the CODE like the drive's banner (#801 round 8).
      const frozen = (err as { code?: string } | null)?.code === TENANT_WRITE_FROZEN_CODE
      toast.error(frozen
        ? t('undo.failedFrozen', { action: getActionDescription(operation) })
        : undoFailureText(t, getActionDescription(operation), err))
      console.error('[Undo] Failed:', err)
    } finally {
      clearTimeout(progressTimer)
      signal.removeEventListener('abort', stopProgressTimer)
      if (progressId !== null) toast.removeToast(progressId)
      if (runningUndoRef.current === run) runningUndoRef.current = null
    }
  }, [stack, toast, t, getActionDescription, resolveUndoTarget])

  const redo = useCallback(async () => {
    // Follows the region, exactly as undo does. Redo reaches the editor and
    // only the editor — file operations have never had one — so outside the
    // editor there is nothing to redo, and re-applying an editor step from
    // the drive's chrome would change a document the user is not looking at.
    //
    // Recoverable, unlike gating on where the last UNDO went: that strands
    // the redo stack for good the first time Ctrl+Z lands on the drive, while
    // this just waits for the user to click back into the editor.
    if (region !== 'editor') return
    if (activeEditorRef.current?.editable && !editorState.canRedo) return
    if (activeEditorRef.current) {
      await activeEditorRef.current.redo()
    }
  }, [editorState.canRedo, region])

  // Global keyboard listener for Ctrl+Z (undo) and Ctrl+Shift+Z (redo)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if typing in a field or contenteditable (native undo handles it) — the rule the command registry's
      // shortcuts share (keyboard.ts) — and, as always here, ANY input (a focused checkbox's Ctrl+Z was never the
      // drive's undo).
      if (isTypingTarget(e.target) || e.target instanceof HTMLInputElement) return

      // Windows reports AltGr as Ctrl+Alt (and a physical Ctrl+Alt IS AltGr
      // there), so on Polish, Croatian and Hungarian layouts a chord meant to
      // type a character was read as Ctrl+Z / Ctrl+Y whenever the browser
      // reported the base letter, running a real undo and swallowing the
      // keystroke (#829). No platform uses Ctrl+Alt+Z for undo, so nothing is
      // lost. The editors' own handlers carry the same guard.
      if (e.altKey) return

      // LOWERCASED, and it matters: with Shift held the browser reports 'Z',
      // so `e.key === 'z'` made the Ctrl+Shift+Z branch below unreachable and
      // redo was keyboard-only through Ctrl+Y. Harmless while no editor had a
      // redo stack; not harmless now that they do, and the database grid has
      // no textarea whose own handler could stand in (those already lowercase
      // it), so on a Mac its redo had no shortcut at all.
      const key = e.key.toLowerCase()
      if ((e.ctrlKey || e.metaKey) && key === 'z') {
        if (e.shiftKey) {
          // Ctrl+Shift+Z → Redo
          e.preventDefault()
          redo()
        } else {
          // Ctrl+Z → Undo
          e.preventDefault()
          // A HELD Ctrl+Z (key repeat) on the drive undoes once: each repeat would otherwise undo the next file
          // operation, or flood "still running" notices while a slow one runs. An editor keeps its repeat.
          if (e.repeat && resolveUndoTarget() !== 'editor') return
          undo()
        }
      }

      // Ctrl+Y → Redo (Windows convention)
      if ((e.ctrlKey || e.metaKey) && key === 'y' && !e.shiftKey) {
        e.preventDefault()
        redo()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [undo, redo, resolveUndoTarget])

  // The menu MUST agree with the keystroke — both read resolveUndoTarget.
  const undoTarget = resolveUndoTarget()
  const canUndo = undoTarget === 'editor' || undoTarget === 'drive'
  const canRedo = region === 'editor' && hasActiveEditor ? editorState.canRedo : false
  const undoLabel = undoTarget === 'editor'
    ? (activeEditorRef.current?.label || null)
    : undoTarget === 'drive' && stack.length > 0
      ? getUndoMenuLabel(stack[0])
      : null
  // Keyed on the SAME condition as `canRedo`, not on "is an editor mounted".
  // A mounted-but-unfocused editor kept its name on the menu, so clicking the
  // explorer showed a greyed-out "Redo Spreadsheet" for a pane that no longer
  // owns the keystroke — the menu naming something the key would not do.
  const redoLabel = canRedo ? (activeEditorRef.current?.label || null) : null

  return (
    <UndoContext.Provider
      value={{
        canUndo,
        canRedo,
        undoLabel,
        redoLabel,
        recordDelete,
        recordMove,
        recordDeleteResult,
        recordMoveResult,
        recordRename,
        recordCopy,
        recordCreateFolder,
        recordAddVersion,
        recordExtract,
        undo,
        redo,
        registerEditor,
        setUndoRegion: setRegion,
        hasActiveEditor,
      }}
    >
      {children}
    </UndoContext.Provider>
  )
}
